import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Tabs, TabsList, TabsTrigger } from "@cronika/ui";
import { AnimatePresence, motion } from "framer-motion";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ShareResultsModal } from "./components/ShareResultsModal";
import { useHistoryList } from "@/hooks/useHistoryList";
import { useSymbolIcon } from "@/shared/lib/hooks/useSymbolIcon";
import { Share } from "@cronika/assets/icons";
import { useTranslation } from "@cronika/locales";
import { DirectionBadge } from "../../components/DirectionBadge";

import { useBetUpdates } from "@/features/common/socket";
import { accountType } from "@/shared/api/browser/accountType";
import { tradingClient } from "@/shared/rpc/clients";
import { userStore } from "@/shared/stores/UserStore";
import { BlockErrorBoundary } from "@/shared/ui/BlockErrorBoundary";
import { CircularLoading } from "@/shared/ui/CircularLoading";

import { ActiveBetCard } from "../bet-module/components/ActiveBetCard";

import { historyStore } from "@/shared/stores/HistoryStore";
import { pairsStore } from "@/shared/stores/PairsStore";
import { pendingBetsStore } from "@/shared/stores/PendingBetsStore";
import type { RealBetEvent } from "@/features/common/socket/types";

import { OPEN_BET_LIFECYCLES } from "@/types/index";
import { AccountType } from "@cronika/proto/common/billing_pb";
import { SortDirection } from "@cronika/proto/common/common_pb";
import { BetLifecycle, BetOutcome, BetType, type BetMessage } from "@cronika/proto/common/trading_pb";

const OPEN_TAB = BetLifecycle.ACCEPTED;
const CLOSED_TAB = BetLifecycle.SETTLED;
type THistoryTab = typeof OPEN_TAB | typeof CLOSED_TAB;

type ActiveBet = BetMessage & {
  timeLeftMs: number;
  progress: number;
  timeLeftHMS: string;
  _uiKey?: string;
  _createdMs?: number;
  _totalMs?: number;
  _targetMs?: number;
};

function msToHMS(ms: number): string {
  if (ms <= 0) return "00:00:00";
  const DAY = 86_400_000;
  if (ms % DAY === 0) {
    const hours = Math.floor(ms / DAY) * 24;
    return `${String(hours).padStart(2, "0")}:00:00`;
  }
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor((totalSeconds % 86_400) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function formatDuration(seconds: number): string {
  if (seconds >= 86400 && seconds % 86400 === 0) return `${seconds / 86400}d`;
  if (seconds >= 3600 && seconds % 3600 === 0) return `${seconds / 3600}h`;
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60}min`;
  return `${seconds}sec`;
}

function formatUSD(value: number): string {
  return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatProfitLoss(item: BetMessage): string {
  if (item.profitLoss) {
    return formatUSD(Math.abs(parseFloat(item.profitLoss)));
  }
  const payoutPct = item.payoutPercent ?? 0;
  return formatUSD(Number(item.amount) * (Number(payoutPct) / 100));
}

const formatTs = (ts: string | undefined | null, tz: string | undefined) => {
  if (!ts) return "—";
  const d = tz ? dayjs(ts).tz(tz) : dayjs(ts);
  return d.format("MMM DD, HH:mm:ss");
};

const ClosedBetItem: FC<{
  item: BetMessage;
  t: (key: string) => string;
  timezone?: string;
}> = ({ item, t, timezone }) => {
  const outcome = item.outcome ?? null;
  const isWon = outcome === BetOutcome.WON;
  const isDraw = outcome === BetOutcome.RETURNED;
  const symbol = item.symbol || "Unknown";

  const pair = pairsStore.pairs.find((p) => p.symbol === item.symbol);
  const pairIcon = useSymbolIcon(pair?.type, pair?.baseAsset, pair?.quoteAsset);
  const [shareOpen, setShareOpen] = useState(false);

  const statusLabel = isWon ? t("bet-status.won") : isDraw ? t("bet-status.returned") : t("bet-status.lost");
  const statusBg = isWon ? "bg-success" : isDraw ? "bg-warning" : "bg-danger";
  const statusTextColor = isWon || isDraw ? "text-black" : "text-white";
  const amountText = isWon ? `+$${formatProfitLoss(item)}` : isDraw ? "$0.00" : `-$${formatUSD(Number(item.amount))}`;
  const amountColor = isWon ? "text-success" : isDraw ? "text-default-500" : "text-danger";

  return (
    <AccordionItem value={item.id} variant="light" className="py-0 w-full overflow-hidden rounded-xl bg-default-500/15">
      <AccordionTrigger className="w-full rounded-xl px-3 py-2 hover:no-underline">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {pairIcon && <img src={pairIcon} alt={symbol} className="h-8 w-8 shrink-0 rounded-full object-cover" />}
            <div className="min-w-0">
              <div className="flex items-center gap-1 text-gray-500">
                <p className="text-default-500 text-[12px]">{symbol}</p>
                <p className="text-default-500 text-[11px]">•</p>
                <p className="text-default-500 text-[11px]">{formatDuration(Number(item.durationSeconds))}</p>
                <p className="text-default-500 text-[11px]">•</p>
                <p className="text-success text-[11px]">{item.payoutPercent}%</p>
              </div>
              <div className="flex items-center gap-1.5">
                <p className="text-[16px] font-semibold">${formatUSD(Number(item.amount))}</p>
                <DirectionBadge betType={item.betType} />
              </div>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <div className="flex items-center">
              <span className={`rounded-full px-1.5 py-0.5 text-[11px] ${statusTextColor} ${statusBg}`}>
                {statusLabel}
              </span>
              {!isDraw && (
                <button
                  type="button"
                  aria-label={t("history.share.trigger-label")}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShareOpen(true);
                  }}
                  className="flex h-6 w-5.5 items-center justify-end rounded-full text-white transition-colors"
                >
                  <Share className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className={`text-[13px] font-bold ${amountColor}`}>{amountText}</p>
          </div>
        </div>
      </AccordionTrigger>
      {!isDraw && <ShareResultsModal bet={item} open={shareOpen} onOpenChange={setShareOpen} />}
      <AccordionContent className="text-small px-3 pb-3 pt-0">
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline gap-1">
            <p className="text-xs text-neutral-500 shrink-0">PnL</p>
            <div className="flex-1 border-b border-dashed border-[#E5FFFB1A] min-w-2 self-end mb-1" />
            <span className={`text-xs ${amountColor}`}>{amountText}</span>
          </div>
          <div className="flex items-baseline gap-1">
            <p className="text-xs text-neutral-500 shrink-0">{t("history.open-time")}</p>
            <div className="flex-1 border-b border-dashed border-[#E5FFFB1A] min-w-2 self-end mb-1" />
            <p className="text-xs text-white shrink-0">{formatTs(item.entryPriceTs, timezone)}</p>
          </div>
          <div className="flex items-baseline gap-1">
            <p className="text-xs text-neutral-500 shrink-0">{t("history.closing-time")}</p>
            <div className="flex-1 border-b border-dashed border-[#E5FFFB1A] min-w-2 self-end mb-1" />
            <p className="text-xs text-white shrink-0">{formatTs(item.settledAt, timezone)}</p>
          </div>
          <div className="flex items-baseline gap-1">
            <p className="text-xs text-neutral-500 shrink-0">{t("history.opening-quote")}</p>
            <div className="flex-1 border-b border-dashed border-[#E5FFFB1A] min-w-2 self-end mb-1" />
            <p className="text-xs text-white shrink-0">{item.entryPrice ?? "—"}</p>
          </div>
          <div className="flex items-baseline gap-1">
            <p className="text-xs text-neutral-500 shrink-0">{t("history.closing-quote")}</p>
            <div className="flex-1 border-b border-dashed border-[#E5FFFB1A] min-w-2 self-end mb-1" />
            <p className="text-xs text-white shrink-0">{item.closePrice ?? "—"}</p>
          </div>
        </div>
      </AccordionContent>
      {item.isCopyTradingTrade && (
        <div className="flex items-center justify-center h-[26px] bg-default-500">
          <p className="text-[11px] font-bold text-black">COPY TRADE</p>
        </div>
      )}
    </AccordionItem>
  );
};

function buildPendingActiveBet(pending: RealBetEvent): ActiveBet {
  const now = Date.now();
  const createdMs = pending.createdAt ? new Date(pending.createdAt).getTime() : now;
  const targetMs = pending.targetTime ? new Date(pending.targetTime).getTime() : createdMs + 60_000;
  const totalMs = Math.max(1, targetMs - createdMs);
  const left = Math.max(0, targetMs - now);
  const progress = Math.min(100, ((totalMs - left) / totalMs) * 100);

  return {
    id: pending.betId,
    symbol: pending.pairSymbol ?? "",
    betType: pending.betType === "put" ? BetType.PUT : BetType.CALL,
    amount: String(pending.amount ?? 0),
    entryPrice: String(pending.entryPrice ?? 0),
    targetTs: pending.targetTime,
    createdAt: pending.createdAt,
    durationSeconds: Math.round(totalMs / 1000),
    minRaisePoints: pending.minRaisePoints != null ? String(pending.minRaisePoints) : undefined,
    payoutPercent: pending.payoutPercent != null ? String(pending.payoutPercent) : undefined,
    _uiKey: pending.uiKey ?? pending.betId,
    _createdMs: createdMs,
    _totalMs: totalMs,
    _targetMs: targetMs,
    timeLeftMs: left,
    progress,
    timeLeftHMS: msToHMS(left),
  } as unknown as ActiveBet;
}

const AnimatedActiveBetCard: FC<{
  bet: ActiveBet;
  index: number;
  animatedKeys: Set<string>;
}> = ({ bet, index, animatedKeys }) => {
  const animationKey = bet._uiKey ?? bet.id;
  const isOptimisticBet = animationKey.startsWith("optimistic-");
  const [animate] = useState(() => isOptimisticBet && !animatedKeys.has(animationKey));

  useEffect(() => {
    if (isOptimisticBet) animatedKeys.add(animationKey);
  }, [animatedKeys, animationKey, isOptimisticBet]);

  return <ActiveBetCard bet={bet} animate={animate} index={index} />;
};

interface HistoryBlockProps {
  // "default" — desktop sidebar: active bets only, always visible, no tabs.
  // "modal" — mobile "Trade History" modal: Open/Closed tabs (the only place
  // mobile can see active bets, since there's no always-visible list there).
  // "flyout" — desktop History flyout: active bets already live in the
  // sidebar, so this shows Closed only, no tabs.
  variant?: "default" | "modal" | "flyout";
  onOverflowChange?: (hasOverflow: boolean) => void;
}

export const HistoryBlock: FC<HistoryBlockProps> = observer(({ variant = "default", onOverflowChange }) => {
  const { t } = useTranslation("trading");
  const userTimezone = userStore.user?.timezone;

  const [currentTab, setCurrentTab] = useState<THistoryTab>(OPEN_TAB);
  const [activeBets, setActiveBets] = useState<ActiveBet[]>([]);
  const [isLoadingActiveBets, setIsLoadingActiveBets] = useState(false);
  const [activeBetsError, setActiveBetsError] = useState(false);
  const settledIdsRef = useRef<Set<string>>(new Set());
  const animatedActiveBetKeysRef = useRef<Set<string>>(new Set());

  const [currentAccountType, setCurrentAccountType] = useState<"demo" | "real">(accountType.get() || "demo");

  const historyListParams = useMemo(
    () => ({
      limit: 20,
      sortBy: "settledAt",
      sortDir: SortDirection.DESC,
      lifecycle: [BetLifecycle.SETTLED],
      accountType: currentAccountType === "real" ? AccountType.REAL : AccountType.DEMO,
    }),
    [currentAccountType],
  );

  const {
    list,
    fetching,
    loadMore,
    hasMore,
    loadingMore,
    prependItem: prependClosedBet,
  } = useHistoryList(historyListParams);

  const buildActive = useCallback((bet: BetMessage): ActiveBet => {
    const now = Date.now();
    const targetTs = bet.targetTs;
    const durationSeconds = bet.durationSeconds || 60;
    const totalMs = durationSeconds * 1000;

    if (!targetTs) {
      return { ...bet, timeLeftMs: 0, progress: 100, timeLeftHMS: "00:00:00" };
    }

    const serverTargetMs = new Date(targetTs).getTime();
    const createdMs = bet.createdAt ? new Date(bet.createdAt).getTime() : serverTargetMs - totalMs;

    const left = Math.max(0, serverTargetMs - now);
    const progress = totalMs > 0 ? Math.min(100, ((totalMs - left) / totalMs) * 100) : 100;

    return {
      ...bet,
      targetTs,
      durationSeconds,
      _targetMs: serverTargetMs,
      _createdMs: createdMs,
      _totalMs: totalMs,
      timeLeftMs: left,
      progress,
      timeLeftHMS: msToHMS(left),
    };
  }, []);

  const loadActiveBets = useCallback(async () => {
    try {
      setIsLoadingActiveBets(true);
      setActiveBetsError(false);

      const response = await tradingClient.listBets({
        lifecycle: [...OPEN_BET_LIFECYCLES],
        sortBy: "createdAt",
        sortDir: SortDirection.DESC,
        pagination: { size: 100, page: 1 },
      });

      const apiBets = (response.data ?? []) as BetMessage[];
      const apiIds = new Set(apiBets.map((b) => b.id));

      setActiveBets((prev) => {
        const previousById = new Map(prev.map((bet) => [bet.id, bet]));
        const socketOnly = prev.filter((b) => !apiIds.has(b.id));
        // Exclude bets that were settled via socket while the request was in-flight.
        const fresh = apiBets.filter((b) => !settledIdsRef.current.has(b.id));
        return [
          ...fresh.map((bet) => ({
            ...buildActive(bet),
            _uiKey: previousById.get(bet.id)?._uiKey ?? bet.id,
          })),
          ...socketOnly,
        ];
      });
    } catch (e) {
      console.error("Failed to load active bets:", e);
      // Показываем ошибку только когда список пустой — если ставки уже показаны через WS, молчим
      setActiveBetsError(true);
    } finally {
      setIsLoadingActiveBets(false);
    }
  }, [buildActive]);

  useEffect(() => {
    return accountType.subscribe((next) => {
      settledIdsRef.current.clear();
      setCurrentAccountType(next);
    });
  }, []);

  useEffect(() => {
    historyStore.fetchBonusClosedBets(currentAccountType);
  }, [currentAccountType]);

  // Handle bet updates via socket context
  const { lastBet } = useBetUpdates();

  useEffect(() => {
    if (!lastBet?.data) return;

    const betData = lastBet.data;
    const currentType = accountType.get();
    const currentProtoType = currentType === "real" ? AccountType.REAL : AccountType.DEMO;
    if (betData.accountType && betData.accountType !== currentProtoType && betData.accountType !== AccountType.BONUS)
      return;

    if (lastBet.type === "bet.accepted") {
      const pendingBet = pendingBetsStore.bets.find((b) => b.betId === betData.id);

      const newBet: BetMessage = {
        ...betData,
        lifecycle: BetLifecycle.ACCEPTED,
        entryPrice: betData.entryPrice || (pendingBet?.entryPrice ? String(pendingBet.entryPrice) : betData.entryPrice),
        ...(!betData.minRaisePoints && pendingBet?.minRaisePoints != null
          ? { minRaisePoints: String(pendingBet.minRaisePoints) }
          : {}),
        ...(!betData.payoutPercent && pendingBet?.payoutPercent != null
          ? { payoutPercent: String(pendingBet.payoutPercent) }
          : {}),
      };

      const activeBet = buildActive(newBet);
      activeBet._uiKey = pendingBet?.uiKey ?? pendingBetsStore.getUiKey(betData.id) ?? betData.id;

      setActiveBets((prev) => {
        const idx = prev.findIndex((b) => b.id === activeBet.id);
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = activeBet;
          return updated;
        }
        return [activeBet, ...prev];
      });
      pendingBetsStore.remove(betData.id);
    }

    if (lastBet.type === "bet.settled") {
      settledIdsRef.current.add(betData.id);
      setActiveBets((prev) => {
        const settledBet = prev.find((b) => b.id === betData.id);
        if (settledBet) {
          prependClosedBet({
            ...settledBet,
            outcome: betData.outcome,
            profitLoss: betData.profitLoss ?? undefined,
            closePrice: betData.closePrice ?? undefined,
            lifecycle: BetLifecycle.SETTLED,
            settledAt: betData.settledAt ?? new Date().toISOString(),
          });
        }
        return prev.filter((b) => b.id !== betData.id);
      });

      userStore.refreshBalance();
    }

    if (lastBet.type === "bet.rejected") {
      pendingBetsStore.remove(betData.id);
    }

    if (lastBet.type === "bet.canceled") {
      pendingBetsStore.remove(betData.id);
      setActiveBets((prev) => prev.filter((b) => b.id !== betData.id));
      userStore.refreshBalance();
    }
  }, [lastBet, prependClosedBet, buildActive]);

  // Active bets progress timer
  useEffect(() => {
    if (currentTab !== OPEN_TAB) return;

    const id = setInterval(() => {
      setActiveBets((prev) => {
        if (prev.length === 0) return prev;

        const now = Date.now();
        return prev.map((b) => {
          if (!b._targetMs || !b._totalMs) return b;

          const left = Math.max(0, b._targetMs - now);
          const progress = b._totalMs > 0 ? Math.min(100, ((b._totalMs - left) / b._totalMs) * 100) : 100;

          return { ...b, timeLeftMs: left, progress, timeLeftHMS: msToHMS(left) };
        });
      });
    }, 1000);
    return () => clearInterval(id);
  }, [currentTab]);

  useEffect(() => {
    if (currentTab === OPEN_TAB) {
      loadActiveBets();
    }
  }, [currentTab, currentAccountType]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || !hasMore || loadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => loadMore(), 300);
    }
  }, [hasMore, loadingMore, loadMore]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!onOverflowChange) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const update = () => onOverflowChange(el.scrollHeight > el.clientHeight + 1);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [onOverflowChange, currentTab, list, historyStore.bonusClosedList.length, activeBets, isLoadingActiveBets]);

  const activeTab = variant === "default" ? OPEN_TAB : variant === "flyout" ? CLOSED_TAB : currentTab;

  return (
    <BlockErrorBoundary minHeight="80px">
      <div className="w-full h-full min-h-0 flex flex-col overflow-hidden">
        <div className="flex flex-col min-h-0 overflow-hidden flex-1">
          {variant === "modal" && (
            <Tabs
              value={String(currentTab)}
              onValueChange={(value) => setCurrentTab(Number(value) as THistoryTab)}
              className="w-full flex-shrink-0 pt-2"
            >
              <TabsList size="sm" className="w-full bg-zinc-900">
                <TabsTrigger value={String(OPEN_TAB)}>{t("history.open-tab")}</TabsTrigger>
                <TabsTrigger value={String(CLOSED_TAB)}>{t("history.closed-tab")}</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {fetching && activeTab === CLOSED_TAB ? (
            <div className="flex-1 flex items-center justify-center">
              <CircularLoading />
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              className={
                variant === "modal"
                  ? "flex-1 min-h-0 overflow-y-auto custom-scroll pr-0.5 pb-37.5 lg:pb-0"
                  : variant === "flyout"
                    ? "flex-1 min-h-0 overflow-y-auto custom-scroll pr-0.5"
                    : "flex-1 min-h-0 overflow-y-auto scroll-x-hide mt-2"
              }
              style={{ touchAction: "pan-y", overscrollBehavior: "contain none" }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.13, ease: "easeInOut" }}
                >
                  {activeTab === OPEN_TAB &&
                    (() => {
                      const realIds = new Set(activeBets.map((b) => b.id));
                      const pendingBets = pendingBetsStore.bets
                        .filter((p) => !realIds.has(p.betId))
                        .map(buildPendingActiveBet);
                      const allBets = [...pendingBets, ...activeBets];
                      return (
                        <>
                          {allBets.length > 0 && (
                            <div className="space-y-2">
                              {allBets.map((bet, index) => {
                                const animationKey = bet._uiKey ?? bet.id;
                                return (
                                  <AnimatedActiveBetCard
                                    key={animationKey}
                                    bet={bet}
                                    index={index}
                                    animatedKeys={animatedActiveBetKeysRef.current}
                                  />
                                );
                              })}
                            </div>
                          )}

                          {isLoadingActiveBets && allBets.length === 0 && (
                            <div className="flex justify-center">
                              <CircularLoading />
                            </div>
                          )}

                          {!isLoadingActiveBets && activeBetsError && allBets.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-12 px-4 gap-3">
                              <p className="text-neutral-500 text-center text-sm">{t("history.load-error")}</p>
                              <button
                                type="button"
                                onClick={() => void loadActiveBets()}
                                className="text-xs text-[#F4A425] hover:underline"
                              >
                                {t("history.retry")}
                              </button>
                            </div>
                          )}

                          {!isLoadingActiveBets && !activeBetsError && allBets.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-12 px-4">
                              <p className="text-default-500 text-center text-sm">{t("history.no-active-bets")}</p>
                            </div>
                          )}
                        </>
                      );
                    })()}

                  {activeTab === CLOSED_TAB && (
                    <>
                      {list.length > 0 || historyStore.bonusClosedList.length > 0 ? (
                        <Accordion type="multiple" className="p-0 flex w-full flex-col gap-2">
                          {[...list, ...historyStore.bonusClosedList]
                            .sort((a, b) => {
                              const ta = a.settledAt ? new Date(a.settledAt).getTime() : 0;
                              const tb = b.settledAt ? new Date(b.settledAt).getTime() : 0;
                              return tb - ta;
                            })
                            .map((item) => (
                              <ClosedBetItem key={item.id} item={item} t={t} timezone={userTimezone} />
                            ))}
                        </Accordion>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                          <p className="text-default-500 text-center text-sm">{t("history.no-bets")}</p>
                        </div>
                      )}
                    </>
                  )}

                  {loadingMore && (
                    <div className="flex justify-center items-center py-4">
                      <CircularLoading />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </BlockErrorBoundary>
  );
});
